/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for realtime notification subscriptions
 */

"use client";

import { realtimeManager } from "@/lib/realtime/realtime-manager";
import { logger } from "@/lib/utils/logger";
import { useEffect } from "react";
import type { NotificationRow, RealtimeStatus } from "./types";

interface UseNotificationsRealtimeOptions {
  enabled: boolean;
  userId: string | null | undefined;
  organizationId: string | null | undefined;
  pushNotificationsEnabled: boolean;
  setNotifications: React.Dispatch<React.SetStateAction<NotificationRow[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  setRealtimeStatus: (status: RealtimeStatus) => void;
}

/**
 * Hook for managing realtime notification subscriptions
 */
export function useNotificationsRealtime({
  enabled,
  userId,
  organizationId,
  pushNotificationsEnabled,
  setNotifications,
  setUnreadCount,
  setRealtimeStatus,
}: UseNotificationsRealtimeOptions) {
  useEffect(() => {
    if (!enabled || !userId || !organizationId || !pushNotificationsEnabled)
      return;

    setRealtimeStatus("connecting");

    // Use enhanced realtime manager with coalescing for notifications
    const handleInsert = (payload: unknown) => {
      const p = payload as { new?: NotificationRow };
      if (p.new) {
        const newNotification = p.new;
        // Only add if it matches user filter
        if (
          newNotification.user_id === userId ||
          newNotification.user_id === null
        ) {
          setNotifications((prev) => {
            // Avoid duplicates
            if (
              prev.some(
                (n) => n.notification_id === newNotification.notification_id,
              )
            ) {
              return prev;
            }
            return [newNotification, ...prev];
          });
          if (!newNotification.read_at) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      }
    };

    const handleUpdate = (payload: unknown) => {
      const p = payload as { new?: NotificationRow; old?: NotificationRow };
      if (p.new) {
        const updatedNotification = p.new;
        // Only update if it matches user filter
        if (
          updatedNotification.user_id === userId ||
          updatedNotification.user_id === null
        ) {
          setNotifications((prev) => {
            const wasRead = prev.find(
              (n: NotificationRow) =>
                n.notification_id === updatedNotification.notification_id,
            )?.read_at;
            const isRead = updatedNotification.read_at;

            // Update unread count
            if (!wasRead && isRead) {
              setUnreadCount((count) => Math.max(0, count - 1));
            } else if (wasRead && !isRead) {
              setUnreadCount((count) => count + 1);
            }

            return prev.map((notification) =>
              notification.notification_id ===
              updatedNotification.notification_id
                ? updatedNotification
                : notification,
            );
          });
        }
      }
    };

    const handleDelete = (payload: unknown) => {
      const p = payload as { old?: NotificationRow };
      if (p.old) {
        const deletedNotification = p.old;
        setNotifications((prev) =>
          prev.filter(
            (n) => n.notification_id !== deletedNotification.notification_id,
          ),
        );
        if (!deletedNotification.read_at) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }
    };

    // Subscribe to notifications table with coalescing
    // Note: We need to handle user_id filtering manually since realtime manager uses org_id filter
    try {
      realtimeManager.subscribe(
        organizationId,
        "notifications",
        (payload) => {
          const p = payload as {
            eventType?: string;
            new?: NotificationRow;
            old?: NotificationRow;
          };
          const eventType =
            p.eventType ??
            (p.new && !p.old
              ? "INSERT"
              : p.new && p.old
                ? "UPDATE"
                : p.old && !p.new
                  ? "DELETE"
                  : "UNKNOWN");

          switch (eventType) {
            case "INSERT":
              handleInsert(payload);
              break;
            case "UPDATE":
              handleUpdate(payload);
              break;
            case "DELETE":
              handleDelete(payload);
              break;
          }
        },
        {
          useCoalescing: true,
          debounceMs: 200, // 200ms debounce for notifications
          throttleMs: 1000, // Max 1 update per second
          maxBatchSize: 20,
        },
      );

      setRealtimeStatus("connected");
    } catch (err) {
      logger.error("[NOTIFICATIONS] Failed to subscribe to realtime:", err);
      setRealtimeStatus("disconnected");
    }

    return () => {
      setRealtimeStatus("disconnected");
      if (organizationId) {
        realtimeManager.unsubscribe(organizationId, "notifications");
      }
    };
  }, [
    enabled,
    userId,
    organizationId,
    pushNotificationsEnabled,
    setNotifications,
    setUnreadCount,
    setRealtimeStatus,
  ]);
}
